/**
 * The Add Outcomes modal's filter state, the facets it offers, and what they
 * narrow (CAE-1553).
 *
 * Its own module for the reason `add-fixtures-filters.ts` gives: the facets, the
 * narrowing they do and the chips that clear them are one fact in three places,
 * and a facet added to the sidebar but forgotten in the chip row is a filter the
 * operator can neither see nor undo.
 *
 * **The facets are built from what is on the contest, not from the catalogue.**
 * The Add Fixtures modal asks `/catalogue/filter-options`, because it browses
 * the feed. This modal's whole source is the attached fixtures, so its
 * competition and sport lists are theirs — an option that matched nothing on
 * screen would be a filter that empties the list for no reason an operator can
 * see. The market facet is the same rule over the markets those fixtures carry.
 *
 * > **No market-category facet.** `08-modals/README.md` asks for one. The feed
 * > has no category — `markets.type` is a copy of the market's own name — and
 * > `shared/modal-add-outcomes.jsx` filters on **market type** instead, which
 * > the catalogue does carry. CAE-1589 holds the open question; the departure is
 * > logged in `docs/design/admin-ui/prototype/README.md`.
 */
import type { CatalogueMarket } from '../../../../api/catalogue';
import type { ContestDetailFixture } from '../../../../api/contests';
import { formatSportLabel } from '../../../../utils/sport-label';
import { marketPickLabel } from './add-outcomes-picks';

export interface AddOutcomesFilterState {
  search: string;
  competition: string;
  sport: string;
  status: string;
  /** A market's own name, which is the only grouping the feed gives us. */
  marketType: string;
  /** Drop the already-added rows instead of drawing them inert. */
  hideOnReel: boolean;
}

/**
 * What the modal opens on: everything, and the already-added outcomes visible.
 *
 * Unlike Add Fixtures there is no `status: 'scheduled'` default. That modal
 * opens on a feed of hundreds where the un-kicked-off ones are what a contest is
 * built from; this one opens on the handful an operator has already attached on
 * purpose, and hiding any of them by default would read as missing data.
 *
 * `hideOnReel` starts off for the same reason the rows are drawn at all: an
 * operator hunting an outcome needs to see that it is already on the reel, which
 * a hidden row cannot tell them. The prototype's sandbox defaults it on; it has
 * mock outcomes that are never searched for.
 */
export const EMPTY_FILTERS: AddOutcomesFilterState = {
  search: '',
  competition: '',
  sport: '',
  status: '',
  marketType: '',
  hideOnReel: false,
};

export const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'live', label: 'Live' },
  { value: 'finished', label: 'Finished' },
  { value: 'settled', label: 'Settled' },
];

export interface AddOutcomesFacets {
  competitions: string[];
  sports: Array<{ value: string; label: string }>;
  marketTypes: string[];
}

/** The facet lists, drawn from the attached fixtures and their markets. */
export const toFacets = (
  fixtures: readonly ContestDetailFixture[],
  marketsByFixture: ReadonlyMap<string, readonly CatalogueMarket[]>,
): AddOutcomesFacets => {
  const competitions = new Set<string>();
  const sports = new Map<string, string>();
  const marketTypes = new Set<string>();

  for (const fixture of fixtures) {
    if (fixture.competitionName) competitions.add(fixture.competitionName);
    if (fixture.sportCode)
      sports.set(fixture.sportCode, formatSportLabel(fixture.sportCode) ?? fixture.sportCode);
    for (const market of marketsByFixture.get(fixture.externalId) ?? []) {
      marketTypes.add(marketPickLabel(market));
    }
  }

  const collate = (left: string, right: string) => left.localeCompare(right);

  return {
    competitions: [...competitions].sort(collate),
    sports: [...sports]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => collate(a.label, b.label)),
    marketTypes: [...marketTypes].sort(collate),
  };
};

/**
 * The markets of one fixture that survive the filters.
 *
 * The market facet narrows twice over: it decides which fixtures are listed at
 * all (below) and which of their markets are drawn. Picking "90 Minutes" and
 * then opening a fixture onto its other 140 markets would be a filter that did
 * not filter.
 *
 * Search reaches market names too, so typing "corner" finds the market without
 * the operator knowing which fixture carries it.
 */
export const visibleMarkets = (
  markets: readonly CatalogueMarket[],
  filters: AddOutcomesFilterState,
  fixtureMatchesSearch: boolean,
): CatalogueMarket[] => {
  const query = filters.search.trim().toLowerCase();

  return markets.filter((market) => {
    const label = marketPickLabel(market);
    if (filters.marketType && label !== filters.marketType) return false;
    /*
     * A search the fixture itself answers leaves its markets alone — an
     * operator who typed a team name wants that fixture's markets, all of them,
     * not the subset whose names happen to contain it too.
     */
    if (query && !fixtureMatchesSearch && !label.toLowerCase().includes(query)) return false;
    return true;
  });
};

/** Whether the fixture's own text answers the search. */
export const fixtureMatchesSearch = (
  fixture: ContestDetailFixture,
  label: string,
  search: string,
): boolean => {
  const query = search.trim().toLowerCase();
  if (query === '') return true;
  return `${label} ${fixture.competitionName ?? ''}`.toLowerCase().includes(query);
};

/**
 * Whether a fixture is listed at all.
 *
 * A fixture whose every market has been filtered out is dropped rather than
 * drawn empty: an expandable row that opens onto nothing is a worse answer than
 * a shorter list, and the market facet's whole point is to find the fixtures
 * that carry something.
 */
export const fixtureIsVisible = (
  fixture: ContestDetailFixture,
  label: string,
  markets: readonly CatalogueMarket[],
  filters: AddOutcomesFilterState,
  /**
   * Markets are still loading — judge the fixture on its own fields alone.
   *
   * This ignores the market facet for that window, which in principle lists
   * fixtures that then vanish as the queries land. In practice it cannot: the
   * modal resets `filters` to {@link EMPTY_FILTERS} on every open, and the
   * facet's options *are* the loaded markets, so there is no way to have chosen
   * a market type before they arrived. A later refetch keeps the cached data and
   * leaves this true throughout.
   */
  marketsLoaded: boolean,
): boolean => {
  if (filters.competition && fixture.competitionName !== filters.competition) return false;
  if (filters.sport && fixture.sportCode !== filters.sport) return false;
  if (filters.status && fixture.status !== filters.status) return false;

  const matchesOwnText = fixtureMatchesSearch(fixture, label, filters.search);
  if (!marketsLoaded) return matchesOwnText;

  const narrowsByMarket = filters.marketType !== '' || filters.search.trim() !== '';
  if (!narrowsByMarket) return true;

  if (matchesOwnText && filters.marketType === '') return true;
  return visibleMarkets(markets, filters, matchesOwnText).length > 0;
};

export interface ActiveFilter {
  filterKey: string;
  value: string;
  clear: () => void;
}

/**
 * The chips above the list. Search is absent for the reason Add Fixtures leaves
 * it out: its input already shows the words that narrowed the list, where a
 * select's choice collapses out of sight.
 */
export const activeFilters = (
  filters: AddOutcomesFilterState,
  set: (next: Partial<AddOutcomesFilterState>) => void,
  sportLabel: (code: string) => string,
): ActiveFilter[] =>
  [
    filters.competition && {
      filterKey: 'Competition',
      value: filters.competition,
      clear: () => set({ competition: '' }),
    },
    filters.sport && {
      filterKey: 'Sport',
      value: sportLabel(filters.sport),
      clear: () => set({ sport: '' }),
    },
    filters.marketType && {
      filterKey: 'Market type',
      value: filters.marketType,
      clear: () => set({ marketType: '' }),
    },
    filters.status && {
      filterKey: 'Status',
      value: STATUS_OPTIONS.find((option) => option.value === filters.status)?.label ?? '',
      clear: () => set({ status: '' }),
    },
  ].filter((entry): entry is ActiveFilter => Boolean(entry));
